import { useCallback, useEffect, useRef } from 'react';
import type { HTMLElementOrNull } from '@/utilities.types';
import { useResizeObserver } from '@/core/hooks/useResizeObserver.hook';
import type { DebounceValue } from '@/core/core.constants';
import { DEBOUNCE } from '@/core/core.constants';
import { useDebounce } from '@/core/hooks/useDebounce.hook';

/**
 * A hook watch for any resize event for an element
 * It uses the ResizeObserver functions that work in most browsers with the exception of IE
 *
 * @param resizeConfig - the configuration object
 * @param element - the DOM element to check for resize events
 * @param callback - the callback function to be used
 * @param callOnLoad - true if the callback should be called on load as well
 * @param callOnDependencyChange - true if the callback should be called when one of the provided dependencies change
 * @param debounce - optional debounce value to use for the callback
 * @return callback for the cleanup
 */
export interface Dimensions {
  oldWidth: number;
  oldHeight: number;
  newWidth: number;
  newHeight: number;
}
interface ResizeConfigParams {
  element?: HTMLElementOrNull;
  elementRef?: React.RefObject<HTMLElement | null>;
  callback: (o: Dimensions) => void;
  callOnLoad?: boolean;
  callOnDependencyChange?: boolean;
  debounce?: DebounceValue;
}

export function useResizeWatcher(
  { element, elementRef, callback, callOnLoad, callOnDependencyChange, debounce = DEBOUNCE.MEDIUM }: ResizeConfigParams,
  dependencies: React.DependencyList = [],
) {
  const { observe, generateHookId } = useResizeObserver();
  const width = useRef(-1);
  const height = useRef(-1);
  const isLoaded = useRef(false);
  const callbackRef = useRef(callback);
  const hookId = useRef<string>(null);
  if (!hookId.current) {
    hookId.current = generateHookId();
  }

  /** The reason this exists in addition to `callbackRef` is an issue stemming from the debouncing mechanism and React's
   *  re-rendering cycle. Imagine:
   *
   *  1. A component using this hook renders and creates `handleResize` with callback A
   *  2. `debouncedHandleResize` is created, closing over `handleResize` (which references callback A)
   *  3. That same component re-renders with new callback B, so callbackRef.current gets updated
   *  4. A new debouncedHandleResize is created, but the old debounced function may still be
   *     pending execution with the now stale callback A reference
   *
   *  Without stableCallbackRef:
   *  - The pending debounced function would execute the old callback A instead of the current callback B
   *
   *  With stableCallbackRef:
   *  - We have a stable wrapper function that never changes identity
   *  - This wrapper always calls callbackRef.current, ensuring we get the most up-to-date callback even if it
   *  executes after debouncing delays
   *
   *  This all arises from the fact that we can't add `callback` as a dependency to `handleResize` because it can
   *  lead to excessive re-renders and re-observations of DOM elements, because sometimes components create new
   *  callbacks excessively or accidentally. Rather than having `handleResize` react to `callback` changes and
   *  counting on users of this hook to manage their own callback stability, we use a stable reference here.
   */
  const stableCallbackRef = useRef<(dimensions: Dimensions) => void>(null);
  if (!stableCallbackRef.current) {
    stableCallbackRef.current = (dimensions) => {
      callbackRef.current(dimensions);
    };
  }

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const handleResize = useCallback((newWidth: number, newHeight: number, forceCallback = false) => {
    const oldWidth = width.current;
    const oldHeight = height.current;

    if (forceCallback || newWidth !== oldWidth || newHeight !== oldHeight) {
      if (forceCallback || !(oldWidth === -1 && oldHeight === -1)) {
        stableCallbackRef?.current?.({
          oldWidth,
          oldHeight,
          newWidth,
          newHeight,
        });
      }
      width.current = newWidth;
      height.current = newHeight;
    }
  }, []);

  const debouncedHandleResize = useDebounce(handleResize, debounce);

  useEffect(() => {
    const currentElement = elementRef ? elementRef.current : element;

    if (currentElement) {
      const cleanup = observe(
        currentElement,
        (entry) => {
          const { width: newWidth, height: newHeight } = entry.contentRect;
          debouncedHandleResize(newWidth, newHeight);
        },
        hookId?.current,
      );

      if (callOnDependencyChange || (callOnLoad && !isLoaded.current)) {
        const { width, height } = currentElement.getBoundingClientRect();
        handleResize(width, height, true);
        isLoaded.current = true;
      }

      return () => {
        debouncedHandleResize.flush();
        cleanup();
      };
    }
  }, [
    element,
    elementRef,
    observe,
    debouncedHandleResize,
    handleResize,
    callOnLoad,
    callOnDependencyChange,
    ...dependencies,
  ]);
}
